import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { preventiviApi, pdfApi } from '../api/client';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  getTermsContentBlockCss,
  getTermsContentBlockPreviewStyleTagInner,
} from '../styles/preventivoTermsContentBlockCss';

function formatUnitaMisura(um) {
  if (!um) return 'CAD';
  const u = String(um).toLowerCase();
  if (u === 'forfait') return 'Forfait';
  return 'CAD';
}

/** Chiarimento ragione sociale (fatturazione CREA FILM) vs marchio operativo AiYR4 */
const FORNITORE_DENOMINAZIONE_AIYR4 =
  'Di seguito nel documento, denominata anche «AiYR4».';

const PreventivoView = () => {
  const { id } = useParams();
  const [preventivo, setPreventivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const termsPreviewScopedCss = useMemo(() => getTermsContentBlockPreviewStyleTagInner(), []);

  useEffect(() => {
    loadPreventivo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPreventivo = async () => {
    try {
      setLoading(true);
      const response = await preventiviApi.getById(id);
      setPreventivo(response.data);
    } catch (error) {
      console.error('Errore caricamento preventivo:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePDFHTML = () => {
    // Il server convertirà automaticamente l'immagine in base64
    const logoUrl = '/images/logo_nero.png';
    
    const deliverablesTotale = preventivo.deliverables?.reduce(
      (sum, d) => sum + (parseFloat(d.subtotale) || 0), 
      0
    ) || preventivo.totale || 0;

    return `
<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <title>${preventivo.numero_preventivo}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500&display=swap');
    
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    @page {
      size: A4;
      margin: 20mm 20mm 25mm 20mm;
    }
    
    body { 
      font-family: 'Inter', sans-serif; 
      font-size: 10pt;
      line-height: 1.7;
      color: #111;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .page { 
      padding: 0;
      max-width: 170mm;
      margin: 0 auto;
    }
    
    .header { 
      margin-bottom: 40px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      page-break-inside: avoid;
    }
    
    .logo-img { 
      max-height: 50px;
      width: auto;
    }
    
    .meta-info {
      text-align: right;
      font-size: 9pt;
      color: #666;
      font-weight: 300;
      letter-spacing: 0.05em;
    }
    
    .doc-title {
      font-family: 'Playfair Display', serif;
      font-size: 18pt;
      margin-bottom: 30px;
      border-bottom: 1px solid #000;
      padding-bottom: 15px;
      page-break-after: avoid;
    }
    
    .parties-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 30px;
      margin-bottom: 40px;
      page-break-inside: avoid;
    }
    
    .party-col {
      page-break-inside: avoid;
    }
    
    .party-col h3 {
      font-family: 'Playfair Display', serif;
      font-size: 10pt;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 12px;
      color: #999;
    }
    
    .party-info { font-size: 10pt; line-height: 1.8; }
    .party-name { font-weight: 600; margin-bottom: 5px; display: block; }
    .party-alias {
      font-size: 9pt;
      color: #666;
      font-style: italic;
      line-height: 1.45;
      margin-bottom: 8px;
      max-width: 42ch;
    }
    
    .section-title {
      font-family: 'Playfair Display', serif;
      font-size: 13pt;
      margin-bottom: 15px;
      margin-top: 30px;
      font-weight: 600;
      page-break-after: avoid;
    }
    
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin: 20px 0 30px 0;
    }
    
    thead {
      page-break-after: avoid;
    }
    
    tbody tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    
    th {
      text-align: left;
      font-family: 'Playfair Display', serif;
      font-size: 9pt;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-bottom: 1.5px solid #000;
      padding: 8px 0;
      color: #111;
    }
    
    td {
      padding: 12px 0;
      border-bottom: 1px solid #eee;
      vertical-align: top;
      font-size: 9.5pt;
    }
    
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    
    .total-section {
      margin-top: 15px;
      border-top: 2px solid #000;
      padding-top: 15px;
      display: flex;
      justify-content: flex-end;
      page-break-inside: avoid;
      page-break-before: avoid;
    }
    
    .total-box {
      min-width: 250px;
      text-align: right;
    }
    
    .total-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 10pt;
    }
    
    .final-total {
      font-family: 'Playfair Display', serif;
      font-size: 13pt;
      font-weight: 700;
      margin-top: 12px;
    }
    
    ${getTermsContentBlockCss()}
    
    .signatures {
      margin-top: 60px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 50px;
      page-break-inside: avoid;
      page-break-before: auto;
    }
    
    .sign-box {
      border-top: 1px solid #000;
      padding-top: 12px;
      font-size: 9pt;
    }
    
    .sign-label {
      font-family: 'Playfair Display', serif;
      margin-bottom: 35px;
      color: #666;
      font-style: italic;
    }
    
    .footer {
      margin-top: 40px;
      text-align: center;
      font-size: 8pt;
      color: #999;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      border-top: 1px solid #eee;
      padding-top: 15px;
      page-break-inside: avoid;
    }
    
    /* Utility per forzare page break */
    .page-break-before { page-break-before: always; }
    .page-break-after { page-break-after: always; }
    .avoid-break { page-break-inside: avoid; }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <img src="${logoUrl}" class="logo-img" alt="AiYR4" />
      <div class="meta-info">
        PREVENTIVO N. ${preventivo.numero_preventivo}<br>
        DATA: ${format(new Date(preventivo.data_creazione), 'dd.MM.yyyy')}
      </div>
    </div>

    <div class="doc-title">
      Accordo di Accettazione
    </div>

    <div class="parties-grid">
      <div class="party-col">
        <h3>Fornitore</h3>
        <div class="party-info">
          <span class="party-name">${preventivo.fornitore_nome}</span>
          <div class="party-alias">${FORNITORE_DENOMINAZIONE_AIYR4}</div>
          ${preventivo.fornitore_indirizzo}<br>
          VAT: ${preventivo.fornitore_vat}
        </div>
      </div>
      <div class="party-col">
        <h3>Cliente</h3>
        <div class="party-info">
          <span class="party-name">${preventivo.cliente_nome}</span>
          ${preventivo.cliente_indirizzo}<br>
          ${preventivo.cliente_piva ? `P.IVA: ${preventivo.cliente_piva}` : ''}
        </div>
      </div>
    </div>

    ${preventivo.oggetto ? `
    <div class="content-block">
      <div class="section-title">Oggetto</div>
      <p>${preventivo.oggetto}</p>
    </div>
    ` : ''}

    ${preventivo.deliverables && preventivo.deliverables.length > 0 ? `
    <table>
      <thead>
        <tr>
          <th style="width: 40%">Descrizione</th>
          <th class="text-center" style="width: 15%">Q.tà</th>
          <th class="text-right" style="width: 20%">Prezzo Unit.</th>
          <th class="text-right" style="width: 25%">Totale</th>
        </tr>
      </thead>
      <tbody>
        ${preventivo.deliverables.map(d => `
          <tr>
            <td>
              <div style="font-weight: 500;">${d.voce}</div>
              <div style="font-size: 8pt; color: #666;">${formatUnitaMisura(d.unita_misura)}</div>
            </td>
            <td class="text-center">${d.quantita}</td>
            <td class="text-right">€ ${d.prezzo_unitario.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
            <td class="text-right">€ ${d.subtotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="total-section">
      <div class="total-box">
        <div class="total-row">
          <span>Subtotale</span>
          <span>€ ${deliverablesTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
        </div>
        ${preventivo.totale_scontato > 0 ? `
        <div class="total-row final-total">
          <span>Totale Scontato</span>
          <span>€ ${preventivo.totale_scontato.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
        </div>
        ` : `
        <div class="total-row final-total">
          <span>Totale</span>
          <span>€ ${deliverablesTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
        </div>
        `}
      </div>
    </div>
    ` : ''}

    ${preventivo.contenuto_html ? `
    <div class="content-block">
      ${preventivo.contenuto_html}
    </div>
    ` : ''}

    <div class="signatures">
      <div class="sign-box">
        <div class="sign-label">Per ${preventivo.cliente_nome}</div>
        <br><br>
      </div>
      <div class="sign-box">
        <div class="sign-label">Per AiYR4</div>
        <br><br>
      </div>
    </div>

    <div class="footer">
      CREA FILM LTD • 2 Twyford Avenue, W3 9QA London • VAT GB250160647
    </div>
  </div>
</body>
</html>
    `;
  };

  if (loading) return <div className="flex justify-center h-screen items-center"><div className="spinner"></div></div>;

  return (
    <div className="max-w-5xl mx-auto px-4">
      {/* Action Bar - Fixed at top */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-4 bg-gray-50/95 dark:bg-[#0f0f0f]/95 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-800/50 mb-8">
        <div className="flex justify-between items-center">
          <Link to="/preventivi" className="flex items-center gap-2 text-xs text-gray-400 hover:text-black dark:hover:text-white transition-colors uppercase tracking-[0.15em]">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Torna ai preventivi
          </Link>
          <div className="flex gap-3">
            <Link
              to={`/preventivi/${id}/edit`}
              className="px-5 py-2 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:border-black dark:hover:border-white transition-all rounded-lg text-xs font-medium"
            >
              Modifica
            </Link>
            <button
              onClick={() => {
                const htmlContent = generatePDFHTML();
                const newWindow = window.open('', '_blank');
                newWindow.document.write(htmlContent);
                newWindow.document.close();
              }}
              className="px-5 py-2 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:border-black dark:hover:border-white transition-all rounded-lg text-xs font-medium"
            >
              Anteprima
            </button>
            <button
              onClick={async () => {
                try {
                  setExporting(true);
                  const htmlContent = generatePDFHTML();
                  const response = await pdfApi.generate(htmlContent, preventivo.numero_preventivo);
                  
                  const downloadUrl = response.data.downloadUrl;
                  window.open(downloadUrl, '_blank');
                } catch (error) {
                  console.error('Errore:', error);
                  alert(`Errore generazione PDF: ${error.response?.data?.error || error.message}`);
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              className="px-6 py-2 bg-black text-white dark:bg-white dark:text-black hover:opacity-90 transition-all rounded-lg text-xs font-semibold disabled:opacity-50"
            >
              {exporting ? 'Generazione...' : 'Scarica PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Card */}
      <div className="bg-white text-black border border-gray-200 p-16 min-h-[1000px] relative shadow-2xl mb-20">
        
        {/* Header */}
        <div className="flex justify-between items-start mb-16 border-b border-black pb-8">
          <div>
            <img src="/images/logo_nero.png" alt="AiYR4" className="h-12 w-auto mb-4" />
          </div>
          <div className="text-right">
            <h2 className="text-xl font-serif mb-2">{preventivo.numero_preventivo}</h2>
            <p className="text-xs text-gray-500 font-light uppercase tracking-wider">
              {format(new Date(preventivo.data_creazione), 'dd MMMM yyyy', { locale: it })}
            </p>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-20 mb-16">
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-6 pb-2">Cliente</h3>
            <div className="font-serif text-lg mb-2">{preventivo.cliente_nome}</div>
            <div className="text-gray-500 font-light leading-relaxed text-sm">
              {preventivo.cliente_indirizzo}
              {preventivo.cliente_piva && <><br />VAT/P.IVA: {preventivo.cliente_piva}</>}
            </div>
          </div>
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-6 pb-2">Fornitore</h3>
            <div className="font-serif text-lg mb-2">{preventivo.fornitore_nome}</div>
            <p className="text-xs text-gray-500 italic leading-snug mb-3 max-w-sm">
              {FORNITORE_DENOMINAZIONE_AIYR4}
            </p>
            <div className="text-gray-500 font-light leading-relaxed text-sm">
              {preventivo.fornitore_indirizzo}<br />
              VAT: {preventivo.fornitore_vat}
            </div>
          </div>
        </div>

        {/* Oggetto */}
        <div className="mb-16">
          <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-4">Oggetto</h3>
          <p className="text-lg font-light leading-relaxed">{preventivo.oggetto}</p>
        </div>

        {/* Tabella Deliverables */}
        {preventivo.deliverables?.length > 0 && (
          <div className="mb-16">
            <table className="w-full mb-12">
              <thead>
                <tr className="border-b border-black">
                  <th className="text-left py-4 font-serif text-xs uppercase tracking-[0.1em] w-1/2">Descrizione</th>
                  <th className="text-center py-4 font-serif text-xs uppercase tracking-[0.1em]">Q.tà</th>
                  <th className="text-right py-4 font-serif text-xs uppercase tracking-[0.1em]">Prezzo</th>
                  <th className="text-right py-4 font-serif text-xs uppercase tracking-[0.1em]">Totale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preventivo.deliverables.map((d, i) => (
                  <tr key={i}>
                    <td className="py-6">
                      <div className="font-medium text-sm">{d.voce}</div>
                      <div className="text-[10px] text-gray-400 mt-1 tracking-wider">{formatUnitaMisura(d.unita_misura)}</div>
                    </td>
                    <td className="text-center py-6 font-light text-sm">{d.quantita}</td>
                    <td className="text-right py-6 font-light text-sm">€ {d.prezzo_unitario.toLocaleString()}</td>
                    <td className="text-right py-6 font-medium text-sm">€ {d.subtotale.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totali */}
            <div className="flex justify-end">
              <div className="w-1/3">
                <div className="flex justify-between py-3 border-b border-gray-100 text-xs uppercase tracking-wider">
                  <span className="text-gray-500">Subtotale</span>
                  <span>€ {preventivo.totale.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-4 text-xl font-serif mt-2 border-b border-black">
                  <span>Totale</span>
                  <span>€ {(preventivo.totale_scontato || preventivo.totale).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Termini: stesso CSS del PDF (modulo preventivoTermsContentBlockCss) */}
        {preventivo.contenuto_html && (
          <>
            <style dangerouslySetInnerHTML={{ __html: termsPreviewScopedCss }} />
            <div className="preventivo-terms-pdf-style mt-10 mb-16 max-w-none">
              <div
                className="content-block"
                dangerouslySetInnerHTML={{ __html: preventivo.contenuto_html }}
              />
            </div>
          </>
        )}

        {/* Footer del documento */}
        <div className="absolute bottom-12 left-0 w-full text-center">
          <div className="w-16 h-[1px] bg-black mx-auto mb-6"></div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
            Crea Film LTD • London • VAT GB250160647
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreventivoView;
